import { Toaster } from '@ixigo/iui/toast';
import { ClientOnly } from '@tanstack/react-router';
import { lazy, Suspense, useEffect, useRef, useState } from 'react';
import { CbccBackButton } from '../cbcc/components/cbcc-back-button';
import { CbccCvpLogger } from '../cbcc/components/cbcc-cvp-logger';
import { CbccPostLoginResume } from '../cbcc/components/cbcc-post-login-resume';
import {
	CBCC_BANNER_CHANGE_EVENT,
	CBCC_BANNER_HEIGHT,
	CbccTopBar,
	useCbccBannerTone,
} from '../cbcc/components/cbcc-top-bar';
import { cbccRouteApi } from '../cbcc/route-api';
import { isApprovedExperience } from '../manage-card/no-redirection';
import { useRestoreJourneyScroll } from '../sections/use-journey-scroll';
import type { DeviceProps } from '../types';
import { CbccPwaReady } from './cbcc-pwa-ready';

// The experience subtrees are LAZY (own chunks) so each visitor downloads
// only the code for the variant the server resolved: web's pinned-canvas
// conductor never ships to phones, the mweb beats never ship to desktops, and
// the manage-card / rejected views load only for those CVP states. The SSR
// HTML still contains the correct variant (React streams lazy components on
// the server), and hydration resolves the SAME chunk because `device` rides
// the dehydrated loader data. The server-rendered markup stays visible while
// the chunk loads (selective hydration) — the null fallback only ever shows
// on client-side navigations.
const TravelCreditCardWeb = lazy(() =>
	import('../web/travel-credit-card-web').then((m) => ({
		default: m.TravelCreditCardWeb,
	})),
);
const TravelCreditCardMweb = lazy(() =>
	import('../mweb/travel-credit-card-mweb').then((m) => ({
		default: m.TravelCreditCardMweb,
	})),
);
const ManageCardPage = lazy(() =>
	import('../manage-card/manage-card-page').then((m) => ({
		default: m.ManageCardPage,
	})),
);
const RejectedView = lazy(() =>
	import('../sections/rejected-view').then((m) => ({
		default: m.RejectedView,
	})),
);
// MWEB-only rejected bottom-sheet (replaces the red top strip on mobile). Lazy
// so web never ships it + its vaul sheet deps.
const CbccRejectedSheet = lazy(() =>
	import('../cbcc/components/cbcc-rejected-sheet').then((m) => ({
		default: m.CbccRejectedSheet,
	})),
);

// Dedicated layout for /travel-credit-card (separate from the app shell).
// Picks the web or mweb parent wrapper based on the device flag; each wrapper
// owns its own layout/assembly. This is where layout decisions live — outside
// the shared section components.
//
// The CVP response (fetched server-side by the route loader) decides the
// experience: when the HOME_SCREEN's status is APPROVED, the approved user gets
// the Manage Card view ON THIS SAME ROUTE; everyone else gets the
// marketing/animation page. Because CVP is resolved in the loader, this
// decision is server-rendered — no first-paint marketing→manage flip.
// (The /travel-credit-card/manage route renders the same view from a fixture —
// that's only for testing.) ManageCardPage is width-responsive (it toggles its
// web/mweb section adapters at the CSS md breakpoint), so it doesn't take the
// `device` flag — unlike the marketing wrappers below.
export function TravelCreditCardLayout({ device }: DeviceProps) {
	const { screens } = cbccRouteApi.useLoaderData();
	const homeScreen = screens?.[0];

	// Coming back from /comparison (a client-side hop now), put the user back
	// where they left the journey. No-ops unless that hop saved a position.
	useRestoreJourneyScroll();

	// Top banner: rejected (red, persistent) / onboarding-resume (yellow,
	// dismissible). Owned HERE so we can (a) shift the page down by the banner
	// height — same handling for both states, so the fixed banner never overlaps
	// content — and (b) hold the onboarding dismiss state.
	const bannerTone = useCbccBannerTone();
	const [bannerDismissed, setBannerDismissed] = useState(false);
	// Both top-strip states are WEB-ONLY now. On mweb the same states surface
	// differently per design: onboarding → the floating CbccPendingNudge above
	// the fixed footer CTA; rejected → the CbccRejectedSheet bottom-sheet below.
	// onboarding hides once dismissed this session; rejected is never dismissible.
	const bannerVisible =
		device === 'web' &&
		(bannerTone === 'rejected' ||
			(bannerTone === 'onboarding' && !bannerDismissed));

	// MWEB rejected bottom-sheet — the mobile replacement for the red top strip
	// (web keeps the CbccTopBar). Shown only for a genuinely REJECTED CVP.
	const showRejectedSheet = device === 'mweb' && bannerTone === 'rejected';

	// When the banner appears/disappears, --cbcc-banner-h flips and the hero
	// column's reserved padding collapses — shifting the GSAP-measured card slot.
	// Notify the hero stage AFTER the DOM commits (layout effect) so it can
	// re-run ScrollTrigger.refresh() against the new geometry. Skip the initial
	// mount (no change yet); only fire on a real subsequent toggle (the dismiss).
	const didMountRef = useRef(false);
	useEffect(() => {
		if (!didMountRef.current) {
			didMountRef.current = true;
			return;
		}
		window.dispatchEvent(
			new CustomEvent(CBCC_BANNER_CHANGE_EVENT, {
				detail: { visible: bannerVisible },
			}),
		);
	}, [bannerVisible]);

	// CVP status drives the experience:
	//   APPROVED, or ONBOARDING_COMPLETE + NO_REDIRECTION → Manage Card view
	//   REJECTED → locked first-screen-only view (no CTA, no scroll) + red bar
	//   else     → the marketing/animation journey (+ optional onboarding bar)
	const page =
		homeScreen && isApprovedExperience(homeScreen) ? (
			<ManageCardPage data={homeScreen} />
		) : homeScreen?.status === 'REJECTED' ? (
			<RejectedView device={device} />
		) : device === 'web' ? (
			<TravelCreditCardWeb />
		) : (
			<TravelCreditCardMweb />
		);

	return (
		<>
			{/* Client-only side-effects keyed off the server-resolved CVP:
			    log the resolved state, and complete the post-login auto-resume
			    (dispatch the user's flow after they log in via a CTA). */}
			<ClientOnly fallback={null}>
				<CbccCvpLogger />
				<CbccPostLoginResume />
				{/* Back-press control for FOREIGN host webviews (the ixigo app +
				    partner PWAs), MOBILE ONLY — those embed this route without browser
				    chrome, so they need an in-page way out; ixiweb/iximweb browsers
				    have their own back and render nothing (the clientId test lives in
				    the component). Client-only: it reads the resolved clientId and
				    calls history/the native quit bridge, none of which can SSR. It
				    covers every CVP state on this route (journey / manage / rejected)
				    because it mounts at the layout, above `page`. */}
				{device === 'mweb' ? <CbccBackButton /> : null}
				{/* Failure nudges: CBCC hooks call showCbccErrorToast on API errors,
				    which render here sliding up from below (see cbcc-toast.ts). The
				    toast inherits this Toaster's position, so we pick it PER DEVICE —
				    bottom-center on mweb, bottom-right on web. Client-only — toasts
				    can't SSR. */}
				<Toaster
					position={device === 'web' ? 'bottom-right' : 'bottom-center'}
					toastOptions={{ duration: 3000 }}
				/>
				{/* MWEB rejected bottom-sheet (mandatory; Okay → ixigo.com). Client-
				    only: it's a portal overlay + does a window redirect. */}
				{showRejectedSheet ? (
					<Suspense fallback={null}>
						<CbccRejectedSheet />
					</Suspense>
				) : null}
			</ClientOnly>
			{/* Banner is a FIXED overlay (see CbccTopBar) — it adds NO document
			    height, so it can't create extra scroll; it FADES OUT as the user
			    scrolls into the journey. We expose its height as --cbcc-banner-h on
			    the page wrapper so the hero chrome can offset its top row below the
			    banner on the first screen (no logo overlap). 0 when no banner. */}
			{bannerVisible && bannerTone ? (
				<CbccTopBar
					tone={bannerTone}
					onDismiss={
						bannerTone === 'onboarding'
							? () => setBannerDismissed(true)
							: undefined
					}
				/>
			) : null}
			<div
				style={
					{
						'--cbcc-banner-h': `${bannerVisible ? CBCC_BANNER_HEIGHT : 0}px`,
					} as React.CSSProperties
				}
			>
				<Suspense fallback={null}>
					{page}
					{/* Share the experience's boundary: a sibling outside it can
					    mount while the page is still waiting for its lazy chunk. */}
					<CbccPwaReady />
				</Suspense>
			</div>
		</>
	);
}
